import { Injectable } from '@angular/core';
import { Enterprise } from '@app/content/models';
import { AuthorizationEntity } from './authentication.models';

const credentialsKey = 'credentials';
const userEnterpriseKey = 'userEnterprise';

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials: AuthorizationEntity | null = null;

  private _rememberMe: boolean = false;

  private _userEnterprise: Enterprise | null = null;

  constructor() {
    const savedCredentials = sessionStorage.getItem(credentialsKey) || localStorage.getItem(credentialsKey);

    const savedUserEnterprise = sessionStorage.getItem(userEnterpriseKey) || localStorage.getItem(userEnterpriseKey);

    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }

    if (savedUserEnterprise) {
      this._userEnterprise = JSON.parse(savedUserEnterprise);
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials && this.credentials.admin;
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): AuthorizationEntity | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(authorizationEntity?: AuthorizationEntity, remember?: boolean) {
    this._credentials = authorizationEntity || null;
    this._rememberMe = remember;

    if (authorizationEntity && authorizationEntity.authorized) {
      const credentials = this.createCredentialsFromAuthEntity(authorizationEntity);
      const storage = remember ? localStorage : sessionStorage;

      storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);

      sessionStorage.removeItem(userEnterpriseKey);
      localStorage.removeItem(userEnterpriseKey);
    }
  }

  setUserEnterprise(enterprise: Enterprise) {
    const storage = this._rememberMe ? localStorage : sessionStorage;

    storage.setItem(userEnterpriseKey, JSON.stringify(enterprise));
  }

  getUserEnterprise(): Enterprise {
    return this._userEnterprise;
  }

  private createCredentialsFromAuthEntity(authorizationEntity: AuthorizationEntity) {
    const username = authorizationEntity.fullName;
    const expiresAt = JSON.stringify(authorizationEntity.expiresIn + new Date().getTime());

    return {
      accessToken: authorizationEntity.accessToken,
      username: username,
      expiresAt: expiresAt,
      admin: authorizationEntity.admin,
      email: authorizationEntity.email,
      EnterpriseCode: authorizationEntity.EnterpriseCode,
      UserCode: authorizationEntity.UserCode,
      uid: authorizationEntity.uid,
      Id: authorizationEntity.Id,
    };
  }
}
